import { ReactNode } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { ContentBlock } from "@/components/wpl/content-block/src/content-block";
import { Link } from "@/libraries/design/components";
import { useLocalizedHref } from "@/libraries/web-framework/src/localization/use-localized-href";
import { Icon } from "@/libraries/wpl/foundations/src/icon";

const contentBlockStyles = css({
    "padding": "0 16px 16px 16px",
    "width": "100%",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".text-section": {
        "flexBasis": "100%",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".contentBlock-linkFlex": {
            marginBlockStart: "auto"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "h2": {
            // eslint-disable-next-line local/ensure-wac-token-usage
            color: "#25385A",
            fontSize: `${token("space.300")}`,
            fontWeight: "500"
        }
    },

    [media.only.sm]: {
        "width": "50%",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".text-section": {
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            h2: {
                fontSize: "2.25rem"
            }
        }
    },
    [media.above.md]: {
        "width": "33.33%",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".text-section": {
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            h2: {
                fontSize: "2.25rem"
            }
        }
    }
});

const contianerStyles = css({
    width: "100%",
    padding: "0 16px",
    [media.above.md]: {
        width: "33.33%",
        marginBlockEnd: "40px"
    }
});

const contentGridStyles = css({
    display: "grid",
    padding: "16px 0",
    gridTemplateColumns: "48px auto",

    [media.only.sm]: {
        gridTemplateColumns: "68px auto"
    },
    [media.above.md]: {
        padding: "unset",
        paddingBlockEnd: "16px",
        gridTemplateColumns: "68px auto"
    }
});

const iconStyles = css({
    [media.below.sm]: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".icon": {
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            svg: {
                width: "36px !important",
                height: "36px  !important"
            }
        }
    }
});

const h3Styles = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#25385A",
    fontSize: "1.25rem",
    fontWeight: "500",
    lineHeight: "1.16",
    margin: "unset",

    [media.above.sm]: {
        fontSize: "1.5rem"
    }
});

const pStyles = css({ lineHeight: "1.4" });

export const SecurityScaleSection = () => {
    const intl = useIntl();

    return (
        <section>
            <h2
                css={css({
                    // eslint-disable-next-line local/ensure-wac-token-usage
                    color: "#25385A",
                    fontSize: `${token("space.300")}`,
                    fontWeight: "500",
                    margin: "unset",
                    textAlign: "center",
                    padding: "30px 16px",

                    [media.above.sm]: {
                        fontSize: "2.25rem",
                        padding: "64px 16px"
                    }
                })}
            >
                {intl.formatMessage({
                    id: "JiraSoftwareFeaturePageTemplate.security-scale.header",
                    defaultMessage: "Designed for security and scale"
                })}
            </h2>
            <div
                css={css({
                    display: "flex",
                    flexWrap: "wrap",
                    maxWidth: "1200px",
                    margin: "auto"
                })}
            >
                <ContentBlock
                    css={contentBlockStyles}
                    pictureProps={{
                        image: {
                            src: "https://wac-cdn.atlassian.com/dam/jcr:fd487e90-1e57-4412-9c23-5fb20f30ae07/Access%20module%20image-01.svg?cdnVersion=ece",
                            alt: ""
                        }
                    }}
                    headerType="h2"
                    headerText={intl.formatMessage({
                        id: "JiraSoftwareFeaturePageTemplate.security-scale.atlassian-access.header",
                        defaultMessage: "Atlassian Access"
                    })}
                    bodyText={intl.formatMessage({
                        id: "JiraSoftwareFeaturePageTemplate.security-scale.atlassian-access.body",
                        defaultMessage:
                            "Sleep soundly with enhanced administration and security backed by SAML SSO, enforced 2-step verification, automated user provisioning, and more."
                    })}
                    ctaProps={{
                        primaryLinkProps: {
                            text: intl.formatMessage({
                                id: "JiraSoftwareFeaturePageTemplate.security-scale.learn-more.link",
                                defaultMessage: "Learn more"
                            }),
                            href: useLocalizedHref("/software/guard"),
                            dataEventProps: {
                                "data-product-key": "jiraSoftwareFeature",
                                "data-event-component": "linkButton",
                                "data-event-container": "cardSection",
                                "data-event": "clicked",
                                "data-label": intl.formatMessage({
                                    id: "JiraSoftwareFeaturePageTemplate.security-scale.learn-more.link",
                                    defaultMessage: "Learn more"
                                }),
                                "data-label-english": "Learn more"
                            }
                        }
                    }}
                />
                <ContentBlock
                    css={contentBlockStyles}
                    pictureProps={{
                        image: {
                            src: "https://wac-cdn.atlassian.com/dam/jcr:8ebe2a91-8722-4bb9-8b36-07b0177bdf88/Premium%20Cloud%20module%20image.svg?cdnVersion=ece",
                            alt: ""
                        }
                    }}
                    headerType="h2"
                    headerText={intl.formatMessage({
                        id: "JiraSoftwareFeaturePageTemplate.security-scale.jsw-premium.header",
                        defaultMessage: "Jira Software Premium"
                    })}
                    bodyText={intl.formatMessage({
                        id: "JiraSoftwareFeaturePageTemplate.security-scale.jsw-premium.body",
                        defaultMessage:
                            "Ensure your team and organization are ready to scale with 99.9% uptime SLAs, unlimited storage, 24x7 Premium Support, and more."
                    })}
                    ctaProps={{
                        primaryLinkProps: {
                            text: intl.formatMessage({
                                id: "JiraSoftwareFeaturePageTemplate.security-scale.learn-more.link",
                                defaultMessage: "Learn more"
                            }),
                            href: useLocalizedHref("/software/jira/premium"),
                            dataEventProps: {
                                "data-product-key": "jiraSoftwareFeature",
                                "data-event-component": "linkButton",
                                "data-event-container": "cardSection",
                                "data-event": "clicked",
                                "data-label": intl.formatMessage({
                                    id: "JiraSoftwareFeaturePageTemplate.security-scale.learn-more.link",
                                    defaultMessage: "Learn more"
                                }),
                                "data-label-english": "Learn more"
                            }
                        }
                    }}
                />
                <div css={contianerStyles}>
                    <div
                        css={css({
                            // eslint-disable-next-line local/ensure-wac-token-usage
                            backgroundColor: "#f4f5f7",
                            display: "flex",
                            flexDirection: "column",
                            containerType: "inline-size",
                            padding: "24px",
                            [media.above.md]: {
                                padding: "32px 24px 48px 32px"
                            }
                        })}
                    >
                        <div css={contentGridStyles}>
                            <div css={iconStyles}>
                                <Icon name="securitycheckmark" size="medium" />
                            </div>
                            <div>
                                <h3 css={h3Styles}>
                                    {intl.formatMessage({
                                        id: "JiraSoftwareFeaturePageTemplate.security-scale.privacy.header",
                                        defaultMessage: "Privacy"
                                    })}
                                </h3>
                                <p css={pStyles}>
                                    {intl.formatMessage(
                                        {
                                            id: "JiraSoftwareFeaturePageTemplate.security-scale.privacy.body",
                                            defaultMessage:
                                                "Investments in <Link>GDPR and Privacy Shield</Link> ensure all data is handled with care."
                                        },
                                        {
                                            Link: (node: ReactNode) => (
                                                // eslint-disable-next-line @atlassian/a11y/anchor-has-content, @atlassian/a11y/link-name
                                                <Link
                                                    type="inline"
                                                    bgColor="bgWhite"
                                                    href={useLocalizedHref(
                                                        "/trust/privacy"
                                                    )}
                                                    text={node as string}
                                                    dataEventProps={{
                                                        "data-product-key":
                                                            "jiraSoftwareFeature",
                                                        "data-event-component":
                                                            "linkButton",
                                                        "data-event-container":
                                                            "cardSection",
                                                        "data-event": "clicked",
                                                        "data-label":
                                                            intl.formatMessage({
                                                                id: "JiraSoftwareFeaturePageTemplate.security-scale.privacy.body.link",
                                                                defaultMessage:
                                                                    "GDPR and Privacy Shield"
                                                            }),
                                                        "data-label-english":
                                                            "GDPR and Privacy Shield"
                                                    }}
                                                />
                                            )
                                        }
                                    )}
                                </p>
                            </div>
                        </div>
                        <div css={contentGridStyles}>
                            <div css={iconStyles}>
                                <Icon name="id" size="medium" />
                            </div>
                            <div className="text-section">
                                <h3 css={h3Styles}>
                                    {intl.formatMessage({
                                        id: "JiraSoftwareFeaturePageTemplate.security-scale.security-compliance.header",
                                        defaultMessage:
                                            "Security and Compliance"
                                    })}
                                </h3>
                                <p css={pStyles}>
                                    {intl.formatMessage(
                                        {
                                            id: "JiraSoftwareFeaturePageTemplate.security-scale.security-compliance.body",
                                            defaultMessage:
                                                "Rest easy with Atlassian’s world-class <Link1>security</Link1> and <Link2>compliance</Link2>."
                                        },
                                        {
                                            Link1: (node: ReactNode) => (
                                                // eslint-disable-next-line @atlassian/a11y/anchor-has-content, @atlassian/a11y/link-name
                                                <Link
                                                    type="inline"
                                                    bgColor="bgWhite"
                                                    href={useLocalizedHref(
                                                        "/software/jira/security"
                                                    )}
                                                    text={node as string}
                                                    dataEventProps={{
                                                        "data-product-key":
                                                            "jiraSoftwareFeature",
                                                        "data-event-component":
                                                            "linkButton",
                                                        "data-event-container":
                                                            "cardSection",
                                                        "data-event": "clicked",
                                                        "data-label":
                                                            intl.formatMessage({
                                                                id: "JiraSoftwareFeaturePageTemplate.security-scale.security-compliance.security.link",
                                                                defaultMessage:
                                                                    "security"
                                                            }),
                                                        "data-label-english":
                                                            "security"
                                                    }}
                                                />
                                            ),
                                            Link2: (node: ReactNode) => (
                                                // eslint-disable-next-line @atlassian/a11y/anchor-has-content, @atlassian/a11y/link-name
                                                <Link
                                                    type="inline"
                                                    bgColor="bgWhite"
                                                    href={useLocalizedHref(
                                                        "/trust/compliance"
                                                    )}
                                                    text={node as string}
                                                    dataEventProps={{
                                                        "data-product-key":
                                                            "jiraSoftwareFeature",
                                                        "data-event-component":
                                                            "linkButton",
                                                        "data-event-container":
                                                            "cardSection",
                                                        "data-event": "clicked",
                                                        "data-label":
                                                            intl.formatMessage({
                                                                id: "JiraSoftwareFeaturePageTemplate.security-scale.security-compliance.compliance.link",
                                                                defaultMessage:
                                                                    "compliance"
                                                            }),
                                                        "data-label-english":
                                                            "compliance"
                                                    }}
                                                />
                                            )
                                        }
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
